import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  CalendarClock, Users, UserMinus, Feather, RefreshCw, AlertTriangle, Clock, Sparkles, ChevronDown, ChevronRight
} from 'lucide-react';
import ReintroductionWeekCard from './ReintroductionWeekCard';
import RecentlyActiveCard from './RecentlyActiveCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Operational admin screen -> English (operational-UI language boundary)
const STATUS_LABEL = {
  confirmed: 'Confirmed',
  assigned: 'Assigned',
  reserved: 'Reserved',
  completed: 'Completed',
  no_show: 'No-show',
  pending: 'Pending',
  approved: 'Confirmed',
};

const REASON_LABEL = {
  exceeded_duration: 'Past planned duration',
  reached_duration: 'Reached planned duration',
  long_running: 'Long-running cycle',
  needs_deload: 'Likely deload',
  no_recent_deload: 'No recent deload',
};

const SectionCard = ({ icon: Icon, title, count, accent, children, testid }) => (
  <div className="glass-card p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-base md:text-lg font-semibold text-white flex items-center gap-2">
        <Icon className={`w-5 h-5 ${accent}`} />
        {title}
      </h3>
      {typeof count === 'number' && (
        <span className={`text-sm font-bold px-2.5 py-0.5 rounded-full bg-white/10 ${accent}`}>
          {count}
        </span>
      )}
    </div>
    {children}
  </div>
);

const EmptyRow = ({ text }) => (
  <p className="text-blue-300/70 text-sm py-3 text-center">{text}</p>
);

const SystemOverview = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showSeasonal, setShowSeasonal] = useState(false);

  const toggleSeasonal = useCallback(async () => {
    const next = !showSeasonal;
    setShowSeasonal(next);
    try { await axios.put(`${API}/admin/ui-settings`, { show_seasonal_tools: next }); }
    catch (e) { console.error('Failed to save seasonal-tools preference', e); }
  }, [showSeasonal]);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/overview`);
      setData(res.data);
    } catch (e) {
      console.error('Error fetching overview:', e);
      setError('Could not load the operations panel.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  useEffect(() => {
    axios.get(`${API}/admin/ui-settings`)
      .then((res) => setShowSeasonal(!!res.data?.show_seasonal_tools))
      .catch(() => {});
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="overview-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12" data-testid="overview-error">
        <p className="text-red-300">{error}</p>
        <button onClick={fetchOverview} className="btn-secondary text-sm mt-4">Retry</button>
      </div>
    );
  }

  const d = data || {};
  const today = d.today_sessions || [];
  const todayIso = d.today || '';
  const upcoming = d.upcoming_sessions || [];
  const active = d.active_clients || { count: 0, clients: [] };
  const inactive = d.inactive_clients || { count: 0, clients: [] };
  const deload = d.deload_candidates || { count: 0, clients: [] };
  const cycleReview = d.cycle_review_candidates || { count: 0, clients: [] };

  const goTo = (clientId, query) => {
    if (!clientId) return;
    window.location.href = `/admin/clients/${clientId}${query ? `?${query}` : ''}`;
  };
  const goToClient = (clientId, tab) => goTo(clientId, tab ? `tab=${tab}` : '');

  // Lightweight pill action button used inside Hoy rows
  const Act = ({ onClick, children, tone = 'default', testid }) => {
    const tones = {
      default: 'bg-white/10 hover:bg-white/20 text-white',
      blue: 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-200',
      amber: 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-200',
      orange: 'bg-orange-500/20 hover:bg-orange-500/30 text-orange-200',
      green: 'bg-green-500/20 hover:bg-green-500/30 text-green-200',
    };
    return (
      <button
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        data-testid={testid}
        className={`text-xs font-medium px-2.5 py-1 rounded-md whitespace-nowrap transition ${tones[tone]}`}
      >
        {children}
      </button>
    );
  };

  return (
    <div className="space-y-6" data-testid="system-overview">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            🏠 Operations Overview
          </h2>
          <p className="text-blue-300 text-sm mt-1">
            Who I'm seeing today · who's training · who needs a decision · who's gone quiet
          </p>
        </div>
        <button
          onClick={fetchOverview}
          className="btn-secondary text-sm flex items-center gap-2"
          data-testid="overview-refresh-btn"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Seasonal / occasional tools — hidden by default, revealed on demand */}
      <div className="glass-card p-3" data-testid="seasonal-tools-section">
        <button
          onClick={toggleSeasonal}
          className="w-full flex items-center justify-between text-left"
          data-testid="seasonal-tools-toggle"
        >
          <span className="flex items-center gap-2 text-sm font-semibold text-blue-100">
            <Sparkles className="w-4 h-4 text-amber-300" /> Seasonal tools
            <span className="text-xs font-normal text-blue-300/60">— occasional actions (e.g. reintroduction after a break)</span>
          </span>
          {showSeasonal ? <ChevronDown className="w-4 h-4 text-blue-300" /> : <ChevronRight className="w-4 h-4 text-blue-300" />}
        </button>
        {showSeasonal && (
          <div className="mt-3" data-testid="seasonal-tools-content">
            <ReintroductionWeekCard />
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="overview-kpi-strip">
        <div className="bg-blue-500/15 border border-blue-400/20 rounded-lg p-4 text-center">
          <p className="text-blue-300 text-xs">Sessions today</p>
          <p className="text-3xl font-bold text-white" data-testid="kpi-today-count">{today.length}</p>
        </div>
        <div className="bg-green-500/15 border border-green-400/20 rounded-lg p-4 text-center">
          <p className="text-green-300 text-xs">Active this week</p>
          <p className="text-3xl font-bold text-white" data-testid="kpi-active-count">{active.count}</p>
        </div>
        <div className="bg-amber-500/15 border border-amber-400/20 rounded-lg p-4 text-center">
          <p className="text-amber-300 text-xs">Inactive (14+ days)</p>
          <p className="text-3xl font-bold text-white" data-testid="kpi-inactive-count">{inactive.count}</p>
        </div>
        <div className="bg-purple-500/15 border border-purple-400/20 rounded-lg p-4 text-center">
          <p className="text-purple-300 text-xs">Review / deload</p>
          <p className="text-3xl font-bold text-white" data-testid="kpi-review-count">
            {deload.count + cycleReview.count}
          </p>
        </div>
      </div>

      {/* Recently active on the app */}
      <RecentlyActiveCard />

      {/* Today + Upcoming */}
      <SectionCard
        icon={CalendarClock}
        title="Today & upcoming sessions"
        accent="text-blue-300"
        testid="overview-today-section"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h4 className="text-blue-200 text-sm font-medium mb-2">Today</h4>
            {today.length === 0 ? (
              <EmptyRow text="No sessions scheduled today" />
            ) : (
              <div className="space-y-2" data-testid="overview-today-list">
                {today.map((s, i) => (
                  <div
                    key={`t-${i}`}
                    className="flex items-center justify-between gap-3 bg-blue-900/30 px-3 py-2 rounded-lg"
                  >
                    <span className="flex items-center gap-2 text-white text-sm font-medium min-w-0">
                      <Clock className="w-3.5 h-3.5 text-blue-300 flex-shrink-0" />
                      <span className="truncate">{s.start_time} · {s.client_name}</span>
                    </span>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Act tone="blue" testid="action-today-planner" onClick={() => goTo(s.client_id, `tab=planner&date=${todayIso}`)}>Open planner</Act>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <h4 className="text-blue-200 text-sm font-medium mb-2">Upcoming</h4>
            {upcoming.length === 0 ? (
              <EmptyRow text="No upcoming sessions" />
            ) : (
              <div className="space-y-2" data-testid="overview-upcoming-list">
                {upcoming.map((s, i) => (
                  <button
                    key={`u-${i}`}
                    onClick={() => goToClient(s.client_id, 'planner')}
                    className="w-full flex items-center justify-between bg-gray-800/40 hover:bg-gray-800/60 transition px-3 py-2 rounded-lg text-left"
                  >
                    <span className="text-white text-sm">{s.date} · {s.start_time} · {s.client_name}</span>
                    <span className="text-xs text-blue-300">{STATUS_LABEL[s.status] || s.status}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </SectionCard>

      {/* Attention grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cycle review candidates */}
        <SectionCard
          icon={AlertTriangle}
          title="Cycle review"
          count={cycleReview.count}
          accent="text-orange-300"
          testid="overview-cycle-review-section"
        >
          {cycleReview.count === 0 ? (
            <EmptyRow text="No cycles need review right now" />
          ) : (
            <div className="space-y-2" data-testid="overview-cycle-review-list">
              {cycleReview.clients.map((c) => (
                <div
                  key={c.client_id}
                  className="bg-orange-900/20 px-3 py-2 rounded-lg"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-white text-sm font-medium truncate">{c.client_name}</span>
                    <span className="text-xs text-orange-300 flex-shrink-0">
                      Week {c.week_of_cycle}{c.expected_weeks ? `/${c.expected_weeks}` : ''}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {(c.reasons || []).map((r) => (
                      <span key={r} className="text-[11px] bg-orange-500/20 text-orange-200 px-2 py-0.5 rounded-full">
                        {REASON_LABEL[r] || r}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-1.5 mt-2">
                    <Act tone="orange" testid="action-cycle-open" onClick={() => goToClient(c.client_id, 'cycles')}>Open cycle</Act>
                    <Act tone="orange" testid="action-cycle-close" onClick={() => goTo(c.client_id, 'tab=cycles&action=close')}>Close cycle</Act>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        {/* Deload candidates */}
        <SectionCard
          icon={Feather}
          title="Deload candidates"
          count={deload.count}
          accent="text-purple-300"
          testid="overview-deload-section"
        >
          {deload.count === 0 ? (
            <EmptyRow text="No client has accumulated enough load for a deload" />
          ) : (
            <div className="space-y-2" data-testid="overview-deload-list">
              {deload.clients.map((c) => (
                <div
                  key={c.client_id}
                  className="bg-purple-900/20 px-3 py-2 rounded-lg"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-white text-sm font-medium truncate">{c.client_name}</span>
                    <span className="text-xs text-purple-300 flex-shrink-0">
                      {c.block_weeks} continuous wks
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-2">
                    <Act testid="action-deload-create" onClick={() => goTo(c.client_id, 'tab=planner&action=deload')}>Create deload</Act>
                    <Act tone="blue" testid="action-deload-planner" onClick={() => goToClient(c.client_id, 'planner')}>Open planner</Act>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        {/* Inactive clients */}
        <SectionCard
          icon={UserMinus}
          title="Inactive (14+ days)"
          count={inactive.count}
          accent="text-amber-300"
          testid="overview-inactive-section"
        >
          {inactive.count === 0 ? (
            <EmptyRow text="All clients have trained recently" />
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="overview-inactive-list">
              {inactive.clients.map((c) => (
                <div
                  key={c.client_id}
                  className="bg-amber-900/15 px-3 py-2 rounded-lg"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-white text-sm font-medium truncate">{c.client_name}</span>
                    <span className="text-xs text-amber-300 flex-shrink-0">
                      {c.days_since != null ? `${c.days_since} days ago` : 'no sessions'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-2">
                    <Act testid="action-inactive-profile" onClick={() => goToClient(c.client_id, 'profile')}>Open profile</Act>
                    <Act tone="amber" testid="action-inactive-book" onClick={() => goToClient(c.client_id, 'planner')}>Book session</Act>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        {/* Active this week */}
        <SectionCard
          icon={Users}
          title="Active this week"
          count={active.count}
          accent="text-green-300"
          testid="overview-active-section"
        >
          {active.count === 0 ? (
            <EmptyRow text="No completed sessions yet this week" />
          ) : (
            <div className="flex flex-wrap gap-2 max-h-72 overflow-y-auto" data-testid="overview-active-list">
              {active.clients.map((c) => (
                <button
                  key={c.client_id}
                  onClick={() => goToClient(c.client_id, 'planner')}
                  className="bg-green-500/15 hover:bg-green-500/25 transition text-green-100 text-sm px-3 py-1.5 rounded-full"
                >
                  {c.client_name}
                </button>
              ))}
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
};

export default SystemOverview;
